import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, set, remove } from "firebase/database";
import { FaImage, FaUpload, FaEdit, FaTrash, FaEye, FaDownload } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import { uploadFile, deleteFile, validateFile } from "../firebase/storage";
import "./AdminProductImages.css";
import { setPageMeta } from "../utils/seo";

const AdminProductImages = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: "Admin Product Images | Banat India",
      description:
        "Upload and manage product images in the Banat India admin panel.",
      keywords: "admin product images, Banat India admin, image management",
    });
  }, []);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    productName: "",
    category: "",
    description: "",
    imageFile: null,
    altText: "",
    price: "",
    discount: "",
    isPrimary: false
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    productName: "",
    category: "",
    description: "",
    altText: "",
    price: "",
    discount: "",
    isPrimary: false
  });

  const categories = [
    "children",
    "adult", 
    "old-age",
    "bamboo",
    "charcoal",
    "miswak",
    "whiteness",
    "travel",
    "acrobat",
    "dino",
    "sweepy",
    "ufo"
  ];

  useEffect(() => {
    loadImages();
  }, []);

  // Debug useEffect to monitor images state changes
  useEffect(() => {
    
    if (images.length > 0) {
      
    }
  }, [images]);

  const showSuccessToast = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorToast = (message) => {
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 4000);
  };

  const loadImages = async () => {
    
    try {
      const database = getDatabase();
      const imagesRef = ref(database, "productImages");
      
      
      
      
      
      const snapshot = await get(imagesRef);
      
      
      
      
      if (snapshot.exists()) {
        const imagesData = snapshot.val();
        
        const imagesList = Object.entries(imagesData).map(([id, image]) => ({
          id,
          ...image
        }));
        
        // Check for invalid URLs and mark them
        const processedImages = imagesList.map(image => ({
          ...image,
          hasBlobUrl: image.imageUrl && image.imageUrl.startsWith('blob:'),
          hasInvalidUrl: image.imageUrl && (
            image.imageUrl.startsWith('blob:') || 
            image.imageUrl.startsWith('http://localhost:3000/') ||
            image.imageUrl.includes('via.placeholder.com')
          )
        }));
        
        
        setImages(processedImages);
              } else {
          // No images exist - set empty array only if we don't already have images
          
          
          
          
          
          setImages(prevImages => {
            if (prevImages.length === 0) {
              
              return [];
            } else {
              
              return prevImages;
            }
          });
        }
      
      setLoading(false);
    } catch (error) {
      
      setLoading(false);
    }
  };





  const handleUpload = async () => {
    if (!uploadForm.imageFile || !uploadForm.productName || !uploadForm.category) {
      showErrorToast("❌ Please fill all required fields");
      return;
    }

    // Validate file
    const validationErrors = validateFile(uploadForm.imageFile);
    if (validationErrors.length > 0) {
      showErrorToast("❌ File validation failed:\n" + validationErrors.join("\n"));
      return;
    }

    setUploading(true);
    
    try {
      const uploadResult = await uploadFile(uploadForm.imageFile);
      const imageUrl = uploadResult.url;
      
      const newImage = {
        productName: uploadForm.productName,
        category: uploadForm.category,
        description: uploadForm.description,
        imageUrl: imageUrl,
        altText: uploadForm.altText || uploadForm.productName,
        price: uploadForm.price ? parseFloat(uploadForm.price) : 0,
        discount: uploadForm.discount ? parseFloat(uploadForm.discount) : 0,
        isPrimary: uploadForm.isPrimary,
        uploadDate: new Date().toISOString(),
        isLocal: uploadResult.isLocal || false
      };

      const database = getDatabase();
      const imagesRef = ref(database, "productImages");
      
      // Get current images
      const snapshot = await get(imagesRef);
      const currentImages = snapshot.exists() ? snapshot.val() : {};
      
      // Add new image
      const newId = `img-${Date.now()}`;
      currentImages[newId] = newImage;
      
      await set(imagesRef, currentImages);
      
      // Update local state with proper processing
      const newImageWithProcessing = {
        id: newId,
        ...newImage,
        hasBlobUrl: newImage.imageUrl && newImage.imageUrl.startsWith('blob:'),
        hasInvalidUrl: newImage.imageUrl && (
          newImage.imageUrl.startsWith('blob:') || 
          newImage.imageUrl.startsWith('http://localhost:3000/') ||
          newImage.imageUrl.includes('via.placeholder.com')
        )
      };
      
      
      setImages(prevImages => {
        const updatedImages = [...prevImages, newImageWithProcessing];
        
        return updatedImages;
      });
      
      // Force a small delay to ensure state is updated before any potential re-renders
      setTimeout(() => {
        
      }, 100);
      
      // Reset form
      setUploadForm({
        productName: "",
        category: "",
        description: "",
        imageFile: null,
        altText: "",
        price: "",
        discount: "",
        isPrimary: false
      });
      
      setShowUploadModal(false);
      
      // Show success message
      showSuccessToast("✅ Image uploaded successfully!");
      
    } catch (error) {
      
      showErrorToast("❌ Error uploading image: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedImage || !editForm.productName || !editForm.category) {
      showErrorToast("❌ Please fill all required fields");
      return;
    }

    try {
      const database = getDatabase();
      const imageRef = ref(database, `productImages/${selectedImage.id}`);
      
      const updatedImage = {
        ...selectedImage,
        productName: editForm.productName,
        category: editForm.category,
        description: editForm.description,
        altText: editForm.altText,
        price: editForm.price ? parseFloat(editForm.price) : 0,
        discount: editForm.discount ? parseFloat(editForm.discount) : 0,
        isPrimary: editForm.isPrimary,
        lastUpdated: new Date().toISOString()
      };
      
      await set(imageRef, updatedImage);
      
      // Update local state
      setImages(images.map(img => 
        img.id === selectedImage.id ? { ...img, ...updatedImage } : img
      ));
      
      setShowEditModal(false);
      setSelectedImage(null);
      showSuccessToast("✅ Image updated successfully!");
      
    } catch (error) {
      
      showErrorToast("❌ Error updating image");
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const database = getDatabase();
      const imageRef = ref(database, `productImages/${imageId}`);
      
      // Get image data to delete from storage
      const image = images.find(img => img.id === imageId);
      
      // Delete from Firebase Storage if it's a Firebase URL
      if (image && image.imageUrl) {
        await deleteFile(image.imageUrl);
      }
      
      await remove(imageRef);
      
      // Update local state
      setImages(images.filter(img => img.id !== imageId));
      
      showSuccessToast("✅ Image deleted successfully!");
      
    } catch (error) {
      
      showErrorToast("❌ Error deleting image");
    }
  };

  const handleClearAllImages = async () => {
    if (!window.confirm("Are you sure you want to delete ALL images? This action cannot be undone.")) return;

    try {
      const database = getDatabase();
      const imagesRef = ref(database, "productImages");
      
      // Delete all images from database
      await remove(imagesRef);
      
      // Clear local state
      setImages([]);
      
      showSuccessToast("✅ All images have been cleared successfully!");
      
    } catch (error) {
      
      showErrorToast("❌ Error clearing all images");
    }
  };

  const filteredImages = images.filter(image => {
    const matchesSearch = image.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCategory === "all" || image.category === filterCategory;
    
    return matchesSearch && matchesFilter;
  });

  const getCategoryBadge = (category) => {
    const colors = {
      children: "#ff6b6b",
      adult: "#4ecdc4",
      "old-age": "#45b7d1",
      bamboo: "#96ceb4",
      charcoal: "#feca57",
      miswak: "#ff9ff3",
      whiteness: "#54a0ff",
      travel: "#5f27cd",
      acrobat: "#00d2d3",
      dino: "#ff9f43",
      sweepy: "#10ac84",
      ufo: "#ee5a24"
    };
    
    return (
      <span 
        className="category-badge"
        style={{ backgroundColor: colors[category] || "#6c757d" }}
      >
        {category}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading images...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Success Toast */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#28a745',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '16px',
          fontWeight: 'bold',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {successMessage}
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#dc3545',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '16px',
          fontWeight: 'bold',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {errorMessage}
        </div>
      )}
      
      <div className="admin-content-header">
        <h1>Product Image Management</h1>
        <div className="admin-actions">
          <button className="upload-btn" onClick={() => setShowUploadModal(true)}>
            <FaUpload /> Upload Image
          </button>
          <button className="export-btn" onClick={() => {
            const data = { images: images, exportDate: new Date().toISOString() };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `product-images-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}>
            <FaDownload /> Export Images
          </button>

          {images.length > 0 && (
            <button className="clear-all-btn" onClick={handleClearAllImages} style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              marginLeft: '10px'
            }}>
              <FaTrash /> Clear All Images
            </button>
          )}
        </div>
      </div>

      <div className="images-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search images by product name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-box">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Images</h3>
          <p className="stat-number">{images.length}</p>
        </div>
        <div className="stat-card">
          <h3>Primary Images</h3>
          <p className="stat-number">{images.filter(img => img.isPrimary).length}</p>
        </div>
        <div className="stat-card">
          <h3>Categories</h3>
          <p className="stat-number">{new Set(images.map(img => img.category)).size}</p>
        </div>
        <div className="stat-card">
          <h3>Recent Uploads</h3>
          <p className="stat-number">{images.filter(img => {
            const uploadDate = new Date(img.uploadDate);
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            return uploadDate > weekAgo;
          }).length}</p>
        </div>
      </div>

      <div className="images-grid">
        {filteredImages.map((image) => (
          <div key={image.id} className="image-card">
            <div className="image-preview">
              <img src={image.imageUrl} alt={image.altText} />
              {image.isPrimary && <span className="primary-badge">Primary</span>}
              {image.hasInvalidUrl && <span className="invalid-url-badge">⚠️ INVALID URL</span>}
            </div>
            <div className="image-info">
              <h4>{image.productName}</h4>
              <p>{image.description}</p>
              <div className="image-meta">
                {getCategoryBadge(image.category)}
                {image.price && image.price > 0 && (
                  <span className="price-tag" style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    ₹{image.price}
                  </span>
                )}
                {image.discount && image.discount > 0 && (
                  <span className="discount-tag" style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    -{image.discount}%
                  </span>
                )}
                <span className="upload-date">
                  {new Date(image.uploadDate).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="image-actions">
              <button 
                className="action-btn view-btn"
                onClick={() => window.open(image.imageUrl, '_blank')}
              >
                <FaEye /> View
              </button>
              <button 
                className="action-btn edit-btn"
                onClick={() => {
                  setSelectedImage(image);
                  setEditForm({
                    productName: image.productName,
                    category: image.category,
                    description: image.description,
                    altText: image.altText,
                    price: image.price ? image.price.toString() : "",
                    discount: image.discount ? image.discount.toString() : "",
                    isPrimary: image.isPrimary
                  });
                  setShowEditModal(true);
                }}
              >
                <FaEdit /> Edit
              </button>
              <button 
                className="action-btn delete-btn"
                onClick={() => handleDelete(image.id)}
              >
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="no-images">
          <FaImage />
          <p>No images found matching your criteria.</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upload New Image</h3>
            <div className="upload-form">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={uploadForm.productName}
                  onChange={(e) => setUploadForm({...uploadForm, productName: e.target.value})}
                  placeholder="Enter product name"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Enter image description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={uploadForm.altText}
                  onChange={(e) => setUploadForm({...uploadForm, altText: e.target.value})}
                  placeholder="Enter alt text for accessibility"
                />
              </div>
              <div className="form-group">
                <label>Price (₹)</label>
                <input
                  type="number"
                  value={uploadForm.price}
                  onChange={(e) => setUploadForm({...uploadForm, price: e.target.value})}
                  placeholder="Enter product price"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  value={uploadForm.discount}
                  onChange={(e) => setUploadForm({...uploadForm, discount: e.target.value})}
                  placeholder="Enter discount percentage"
                  min="0"
                  max="100"
                  step="1"
                />
              </div>
              <div className="form-group">
                <label>Image File *</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUploadForm({...uploadForm, imageFile: e.target.files[0]})}
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={uploadForm.isPrimary}
                    onChange={(e) => setUploadForm({...uploadForm, isPrimary: e.target.checked})}
                  />
                  Set as primary image
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
              <button 
                className="upload-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload Image"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Image</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={editForm.productName}
                  onChange={(e) => setEditForm({...editForm, productName: e.target.value})}
                  placeholder="Enter product name"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Enter image description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Alt Text</label>
                <input
                  type="text"
                  value={editForm.altText}
                  onChange={(e) => setEditForm({...editForm, altText: e.target.value})}
                  placeholder="Enter alt text for accessibility"
                />
              </div>
              <div className="form-group">
                <label>Price (₹)</label>
                <input
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                  placeholder="Enter product price"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  value={editForm.discount}
                  onChange={(e) => setEditForm({...editForm, discount: e.target.value})}
                  placeholder="Enter discount percentage"
                  min="0"
                  max="100"
                  step="1"
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.isPrimary}
                    onChange={(e) => setEditForm({...editForm, isPrimary: e.target.checked})}
                  />
                  Set as primary image
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="edit-btn"
                onClick={handleEdit}
              >
                Update Image
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProductImages; 